/**
 * @file plugins/parentAware.js
 * @author Jake Stark
 * @desc Child Demographics report plugin

*/

/**
 * @module plugins/parentAware
 * @desc Child Demographics report plugin
 */

define('plugins/parentAware',["text!plugins/templates/parentAware_ChildrenRatedProg_table.html!strip",
        "text!plugins/templates/parentAware_ChildrenScholarships_table.html!strip",
        "text!plugins/templates/parentAware_ChildrenWithCCAP_table.html!strip",
        "models", "cardboard/models", "cardboard/collections", "cardboard/pluginPieces"
    ],
    function(childrenRatedProgTableTemplate,
        childrenScholarshipsTableTemplate, childrenWithCCAPTableTemplate,
        ECLDSModels, CardboardModels, CardboardCollections, PluginPieces) {

        console.log("MODULE: parentAware");

        var exports = {

            // other names by which this plugin is known. this exists for
            // WebFOCUS shim compatibility
            //aliases: ["demographic", "demographics"],
            options: {
                //noDefaultOrg: true,
                orgDetailPane: false,
                noMapSearch: true,
                noOrgFinderSchool: true,
                orgFinderDistrict: true,
                forcedSummaryFilters: ["FISCAL_YEAR"],
                definitionsECLDS: true
            },

            // the options available to these filters will be populated
            // from the database, via the below data sources
            filters: {
                // no filters!
            },

            // each of these is the configuration that gets passed to a
            // DataBoxPaneView.
            panes: [{
                    title: "Children in Rated Programs (CCAP/Scholarships)",
                    explanationText: "<p>This chart shows children enrolled in Parent Aware Rated programs based on either the receipt of Child Care Assistance Program (CCAP) or Early Learning Scholarship at any point during the selected school year. This is an unduplicated count, with each child being shown by funding type (Only CCAP, Only Scholarship, or Both CCAP and Scholarship) at the Star-Level of the program the child attended for the longest duration.  Duration is calculated by the number of hours by months attended.  </p><p>In fiscal year 2017 between July 1, 2016 and June 30, 2017, Early Learning Scholarships could only be used at programs with a Parent Aware rating of Three or Four Stars. However, children with scholarships awarded prior to July 1, 2016 attending a program with a rating of participating, one star or two stars were allowed to continue to keep their program selection until the scholarship was renewed. This policy was changed to revert back to allowing scholarships to be used at any Parent Aware rated or participating program. On July 1, 2024, the policy will again change to only allow scholarships at programs with a Parent Aware Rating of Three or Four Stars.  </p>",
                    openByDefault: true,

                    views: [{
                            type: "template",
                            template: "<p>NOTE: Children included must be younger than 5 years old on 9/1 of the selected school year, attending a Parent Aware Rated program, and using either Child Care Assistance Program (CCAP) or Early Learning Scholarships funds.    <a class='infoButton' style='display: inline; position: relative;'  href='#'>More information, including calculation methods</a>. </p> <button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                        },

                           {
                        // SETUP
                        // all globs go into one series
                        // each glob has only one row = one data point
                        type: "column",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.childrenInRatedPrograms",
                        convertDataHashToArray: true,
                        yAxisMax: 100,
                        title: "",

                        // "pointsWithinRows": given that the data is
                        // two-dimensional (meaning an array of arrays, or
                        // similar structure, implying that we have more than
                        // one series to plot) each series is contained in a
                        // "row", like so:
                        //   +---------+
                        //   | X X X X |
                        //   | . . . . |
                        //   | . . . . |
                        //   | . . . . |
                        //   +---------+
                        dataShape: "pointsAcrossRows",

                        // since in this case there's only one dimension, due to
                        // there being only one series to plot, it looks like
                        // this:
                        //   +---------+
                        //   | X X X X |
                        //   +---------+
                        dataDimensions: 2,

                        // DISPLAY
                        unitString: "%",
                        unitDec: 1,
                        disableLegendClick: false,
                        exportToPDF: true,
                        hideContext: true,
                        xAxisLabelPath: "starRating",
                        //  tooltipTemplate: "{{programName}}<br /><br />" + "<strong>{{formattedPercent}} </strong><br />",
                        //  colorByPoint: false,
                        showLegend: true,

                        // SERIES
                        series: [{
                            valuePath: "legend[0].percent",
                            seriesDisplayName: "CCAP",
                         //   color: "orange"
                        }, {
                            valuePath: "legend[1].percent",
                            seriesDisplayName: "Scholarships",
                         //   color: "purple"
                        }, {
                            valuePath: "legend[2].percent",
                            seriesDisplayName: "Both",
                         //   color: "purple"
                        }],

                        errorConditions: [PluginPieces.standardErrorCondition]
                    },
                    {
                        type: "template",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.childrenInRatedPrograms",
                        template: childrenRatedProgTableTemplate,
                        errorConditions: [PluginPieces.silentStandardErrorCondition]
                    }]
                }, {
                    title: "Children with Scholarship by Site and Rating",
                    explanationText: "<p>This chart shows children receiving Early Learning Scholarship based on their primary placement type during the school year. The data column of Tribally Licensed Programs represents child care centers and family child care providers licensed under Tribal authority. Across the state, this represents 61 programs. If the program was affiliated with either Head Start/Early Head Start or a Public School Pre-Kindergarten program, the child was counted in that category. Prior to July 1, 2016, parents could use scholarships at any program participated or rated by Parent Aware. </p><p>In fiscal year 2017 between July 1, 2016 and June 30, 2017, Early Learning Scholarships could only be used at programs with a Parent Aware rating of Three or Four Stars.  However, children with scholarships awarded prior to July 1, 2016 attending a program with a rating of participating, one star or two stars were allowed to continue to keep their program selection until the scholarship was renewed. This policy was changed to revert back to allowing scholarships to be used at any Parent Aware rated or participating program. On July 2020, the policy will again change to only allow scholarships at programs with a Parent Aware Rating of Three or Four Stars. </p><p>This is not a complete picture of all children enrolled into Parent Aware rated programs, but a snapshot of this population. In 2015, all eligible program types were able to apply to be Parent Aware Rated. Head Start/Early Head Start can only be granted a four star rating.  </p>",
                    openByDefault: false,

                    views: [{
                            type: "template",
                            template: "<p>NOTE: This chart shows the percentage of children served with Early Learning Scholarship Funding by the Parent Aware Rating and Program type.  </p> <button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                        },
                        {
                        // SETUP
                        // all globs go into one series
                        // each glob has only one row = one data point
                        type: "stackedColumn",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.childrenwithScholarshipBySiteAndRating",
                        convertDataHashToArray: true,
                        yAxisMax: 100,
                        title: "",

                        // "pointsWithinRows": given that the data is
                        // two-dimensional (meaning an array of arrays, or
                        // similar structure, implying that we have more than
                        // one series to plot) each series is contained in a
                        // "row", like so:
                        //	 +---------+
                        //	 | X X X X |
                        //	 | . . . . |
                        //	 | . . . . |
                        //	 | . . . . |
                        //	 +---------+
                        dataShape: "pointsAcrossRows",

                        // since in this case there's only one dimension, due to
                        // there being only one series to plot, it looks like
                        // this:
                        //	 +---------+
                        //	 | X X X X |
                        //	 +---------+
                        dataDimensions: 2,

                        // DISPLAY
                        // categoryFieldName: "entityType",
                        legendReversed: false,
	                    unitString: "%",
	                    exportToPDF: true,
	                    hideContext: true,
	                    stackLabels: false,

	                    showLegend: true,
	                    disableLegendClick: false,
                        xAxisLabelPath: "siteType",
                        //  tooltipTemplate: "{{programName}}<br /><br />" + "<strong>{{formattedPercent}} </strong><br />",
                        //  colorByPoint: false,
                        
                        // SERIES
                        series: [{
                                valuePath: "legend[0].percent",
                                seriesDisplayName: "Four-Star"
                            }, {
                                valuePath: "legend[1].percent",
                                seriesDisplayName: "Three-Star"
                            },
                            {
                                valuePath: "legend[2].percent",
                                seriesDisplayName: "Two-Star"
                            }, {
                                valuePath: "legend[3].percent",
                                seriesDisplayName: "One-Star"
                            }
                        ],
                        errorConditions: [PluginPieces.standardErrorCondition]
                    }, {
                        type: "template",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.childrenwithScholarshipBySiteAndRating",
                        template: childrenScholarshipsTableTemplate,
                        errorConditions: [PluginPieces.silentStandardErrorCondition]
                    }]
                }, 
                {
                    title: "Children with CCAP by Site and Rating",
                    explanationText: "<p>This chart shows children younger than 5 years old on 9/1 of the selected school year, who are using the Child Care Assistance Program (CCAP) by Parent Aware Rated program type they attended during the school year referenced. If a child attended more than one program type or rating level, the child was only counted once based on the Star-Level of the site that the child received the highest duration of services. </p><p>The data column of Tribally Licensed Programs represents child care centers and family child care providers licensed under Tribal authority. Across the state, this represents 47 programs.</p><p>Head Start/Early Head Start and Public School Pre-Kindergarten programs are only eligible to receive a Four-Star Rating.  Child Care Assistance can sometimes pay part of a Head Start or Public School Pre-Kindergarten program's cost if a child is receiving full-day services. </p><p>CCAP reimbursement rates: Starting in March 2014, programs rated with Four Stars receive a 20% Quality Differential Rate; Programs with Three Stars receive a 15% Quality Differential Rate.  </p><p>This is not a complete picture of all children enrolled into Parent Aware rated programs, but a snapshot of this population. In 2015, all eligible program types were able to apply to be Parent Aware Rated. </p>",
                    openByDefault: false,

                    views: [{
                            type: "template",
                            template: "<p>NOTE:  Children included in this chart must be 5 years old by 9/1 of the selected school year, attending a Parent Aware Rated program and using Child Care Assistant Program (CCAP) funds.    <a class='infoButton' style='display: inline; position: relative;'  href='#'>More information, including calculation methods</a>. </p><button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button> "
                        },{
                        // SETUP
                        type: "stackedColumn",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.childrenwithCCAPbySiteAndRating",

                        // "pointsAcrossRows": given that the data is
                        // two-dimensional (meaning an array of arrays, or
                        // similar structure, implying that we have more than
                        // one series to plot) each series is contained in a
                        // "column", like so:
                        //   +---------+
                        //   | X . . . |
                        //   | X . . . |
                        //   | X . . . |
                        //   | X . . . |
                        //   +---------+
                        // To wrap your head around this, watch iteration in your debugger in
                        // pointsAcrossRows() in transforms.js.
                        dataShape: "pointsAcrossRows",
                        convertDataHashToArray: true,
                        dataDimensions: 2,
                        unitDec: 1,
                        title: "",

                        // DISPLAY
                        xAxisLabelPath: "siteType",
                        legendReversed: false,
                        unitString: "%",
                        exportToPDF: true,
                        hideContext: true,
                        stackLabels: false,

                        showLegend: true,
                        disableLegendClick: false,

                        // SERIES (only one series, this is a simple graph)
                       series: [{
                                valuePath: "legend[0].percent",
                                seriesDisplayName: "Four-Star"
                            }, {
                                valuePath: "legend[1].percent",
                                seriesDisplayName: "Three-Star"
                            },
                            {
                                valuePath: "legend[2].percent",
                                seriesDisplayName: "Two-Star"
                            }, {
                                valuePath: "legend[3].percent",
                                seriesDisplayName: "One-Star"
                            }
                        ],
                        errorConditions: [PluginPieces.standardErrorCondition]
                    }, {
                        type: "template",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.childrenwithCCAPbySiteAndRating",
                        template: childrenWithCCAPTableTemplate,
                        errorConditions: [PluginPieces.silentStandardErrorCondition]
                    }]
                },

                {
                    title: "For More Information",
                    visibleByDefault: true,
                    openByDefault: false,
                    views: [{
                        type: "template",
                        template: "<p> These charts show intersections between funding sources and children receiving Early Learning Scholarships.  Charts also show children enrolled in Parent Aware based on receipt of the funding sources: Early Learning Scholarships and Child Care Assistance (CCAP).   </p><h2> Glossary</h2><p><strong> CTSTR</strong>: Counts Too Small To Report - when results are less than 10. <br/><strong> No Data Available</strong></strong>: No data is available for the selected criteria. <br/><strong> Scholarship</strong>:  Early learning scholarships are awarded to eligible three and four year old children to support access to quality early education and care programs for Minnesota's low income children by bridging funding gaps and decreasing barriers. Scholarships may be used in eligible Parent Aware rated early learning programs. <br/><strong>Parent Aware</strong>:  Parent Aware is Minnesota's Quality Rating System for Early Learning Programs.  Programs receive a rating of One, Two, Three or Four Stars.  The ratings are based on demonstrated use of best practices that best prepare children for Kindergarten.  <br/><strong>Child Care Assistance Program</strong>: (CCAP) helps make quality child care affordable by providing financial assistance to help families with low incomes pay for child care. <br/><strong>Head Start/Early Head Start</strong>: Head Start supports the comprehensive development of children from birth to age 5 to promote school readiness for young children from low-income families. Head Start services include early learning, health, and family well-being. <br/><strong>Licensed Child Care Center</strong>: Licensed child care centers care for infants, toddlers, preschoolers and school-age children in a care center, community centers or place of worship. State law regulates the number of children who may attend based on the number of staff working at the center. Children are typically cared for in groups divided by age. <br/><strong>Licensed Family Child Care</strong>: Licensed family child care providers may care for infants, toddlers, preschoolers and school-age children in their homes. State law limits the number of children a care provider may look after. <br/><strong>MN District Preschool</strong>: Program for children age three to kindergarten entrance with the purpose of preparing children to enter kindergarten. As of June 2021, Voluntary Prekindergarten (VPK) and School Readiness Plus (SRP) data are included in the MN District Preschool data provided by MDE. School Readiness data begin with the 2013-2014 school year; VPK data begin with the 2016-2017 school year; SRP data begin with the 2017-2018 school year. <br/><strong>Tribally Licensed</strong>:<strong> Child Care </strong>Providers <strong>licensed</strong> by a Federally Recognized <strong>Tribe</strong>. </p><p><strong>To learn more:</strong><br/>Find a program or find how to become rated:  <a href='http://parentaware.org/'  target='_blank'>Parent Aware</a> <br/>Evaluations of Parent Aware: <a href='http://www.pasrmn.org/work/research'  target='_blank'>Evaluations</a> <br/> MN Department of Human Services Public Reporting:  <a href='http://www.dhs.state.mn.us/main/idcplg?IdcService=GET_DYNAMIC_CONVERSION&RevisionSelectionMethod=LatestReleased&dDocName=id_008779'  target='_blank'>Parent Aware Reports</a></p><p><h2>Selection Criteria Definitions</h2><p><strong> School County</strong>: County of the location of public school of enrollment. <br/><strong>Economic Development Region</strong>: The 13 economic development regions within the state of Minnesota as defined by the Department of Employment and Economic Development (DEED). <br/><strong>School Year</strong>: Period of time that covers July 1 of one year through June 30 of the following. <br/><strong>Race/Ethnicity</strong>: Federal Race/Ethnicity categories are as reported by the child's education record and align with the federal <a href='https://www.govinfo.gov/content/pkg/FR-1997-10-30/pdf/97-28653.pdf' target='_blank'>Office of Management and Budget's standards</a>. <i>Note: While state race data is collected in K-12 education records and utilized in ECLDS Kindergarten and Third Grade reports, the same data is not yet universally collected by early care and education programs.</i><br/><strong>Child Protection Involvement</strong>: Child Protection involvement means the identified child was in a family where a child protection response was completed. A child protection response is completed when an allegation of child abuse (maltreatment) by parents/caretakers in is determined to meet statutory guidelines for a child protection assessment or investigation. Child protection intervenes with families, according to Minnesota State Statute and the <a href='https://edocs.dhs.state.mn.us/lfserver/Public/DHS-5144-ENG'  target='_blank'>Minnesota Child Maltreatment Screening Guidelines</a>, to address family functioning issues and ensure that child victims of maltreatment are safe. <br/><strong> Low Birth Weight</strong>: An infant who is born weighing less than 2,500 grams. Data is obtained from the child's birth record.  <br/><strong>Preterm Birth</strong>: An infant who is born before 37 weeks gestational age as calculated from the imputed gestational age based on last menstrual period. Data is obtained from the child's birth record.</p><p> For more information, open the <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=mde034818&RevisionSelectionMethod=latestReleased&Rendition=primary'  target='_blank'>ECLDS Data Guide. </a></p>"
                    }]
                }
            ],

            filterSources: {
                orgs: {
                    modelType: ECLDSModels.ECLDSOrgList,
                    //url: "/data/state.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_orgid_parentAware.fex",
                    noViewParams: true,
                    enabled: true
                },
                filters: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_parentAware_EcoDevRegion.fex"
                    },
                    enabled: true,
                    noViewParams: false
                },
                
                filters2: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_fiscal_year_parentAware.fex",
                    enabled: true,
                    defaultParams: ["FISCAL_YEAR=2021"],
                    modelType: CardboardModels.FilterData
                },

                filters3: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    //    defaultParams: ["FISCAL_YEAR=2014"],
                    urls: {
                        //filterForReport: "data/filters.json"
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_parentAware.fex"
                    },

                    // specify the order of filters, by name (filterUrl in database terms)
                    // filterOrder: ["IDEA_COLLECTIONS", "ECSE_OUTCOMES", "MOMS_AGE_AT_CB", "MOMS_EDLVL_AT_CB", "CR"],

                    enabled: true,
                    noViewParams: false
                },

                demographicCategories: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_parentAware_nbr2.fex",
                    enabled: true,
                    modelType: CardboardCollections.DemoCatProducer,
                    noViewParams: false
                }
            },

            dataSources: {
                data: {
                    modelType: CardboardModels.Model,
                    //url:"/data/tempChildDemo.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_sec_p_parentAware.fex",
                    yearPath: "meta.fiscalYear", // where to look in the JSON for the year
                    //	defaultParams: {
                    //		orgId: 999999000,
                    //      COHORTID: 2014
                    //	},
                    enabled: true
                }
            }
        };

        return exports;
    });
